<template>
  <Dialog v-model="visible" :title="form.id ? '编辑模板' : '新增模板'" width="720px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="96px">
      <el-form-item label="主分类" prop="activityKey">
        <el-select
          v-model="form.activityKey"
          class="!w-full"
          clearable
          filterable
          placeholder="请选择活动分类（与「分类管理」位标记一致）"
          @change="onActivityKeyChange"
        >
          <el-option
            v-for="cat in categoryOptions"
            :key="cat.id"
            :label="categoryLabel(cat)"
            :value="cat.id"
          />
        </el-select>
        <div class="text-12px text-gray-400 mt-4px">
          写入字段 <code>activityKey</code>，活动市场侧边栏与分类列表按此筛选
        </div>
      </el-form-item>
      <el-form-item v-if="subTypeOptions.length" label="子分类">
        <el-select v-model="form.categoryKey" class="!w-full" clearable placeholder="可选，如纯抽奖/益智类">
          <el-option v-for="sub in subTypeOptions" :key="sub.id" :label="sub.name" :value="sub.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="名称" prop="name">
        <el-input v-model="form.name" placeholder="模板展示名称" />
      </el-form-item>
      <el-form-item label="简介">
        <el-input v-model="form.brief" type="textarea" :rows="2" placeholder="列表页副标题" />
      </el-form-item>
      <el-form-item label="关键词">
        <el-input v-model="form.keyword" placeholder="搜索用，可与名称不同" />
      </el-form-item>
      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item label="节日">
            <el-select v-model="form.festivalKey" class="!w-full" clearable filterable placeholder="可选">
              <el-option v-for="f in festivalOptions" :key="f.key" :label="f.name" :value="f.key" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="场景">
            <el-select v-model="form.sceneKey" class="!w-full" clearable filterable placeholder="可选">
              <el-option v-for="s in sceneOptions" :key="s.key" :label="s.name" :value="s.key" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="行业">
        <el-select v-model="form.tradeId" class="!w-full" clearable filterable placeholder="可选">
          <el-option v-for="t in industryOptions" :key="t.key" :label="t.name" :value="t.key" />
        </el-select>
      </el-form-item>
      <el-form-item label="封面图片">
        <UploadImg
          v-model="form.coverUrl"
          directory="market/template/cover"
          height="120px"
          width="200px"
          :file-type="['image/jpeg', 'image/png', 'image/webp', 'image/gif']"
        />
        <div class="text-12px text-gray-400 mt-4px">建议 16:10，支持 jpg/png/webp/gif，不超过 5MB</div>
      </el-form-item>
      <el-form-item label="预览视频/动图">
        <UploadFile
          v-model="form.previewGifUrl"
          directory="market/template/preview"
          :limit="1"
          :file-type="['mp4', 'webm', 'ogg', 'mov', 'gif']"
          :file-size="100"
          material-media-type=""
          replace-on-exceed
        />
        <div class="text-12px text-gray-400 mt-4px">卡片悬停预览，支持 mp4/webm/mov/gif，不超过 100MB</div>
        <img
          v-if="form.previewGifUrl && isGifUrl(form.previewGifUrl)"
          :src="form.previewGifUrl"
          alt="预览动图"
          class="mt-8px"
          style="max-width: 100%; max-height: 180px; border-radius: 8px"
        />
        <video
          v-else-if="form.previewGifUrl && isVideoUrl(form.previewGifUrl)"
          :src="form.previewGifUrl"
          class="mt-8px"
          controls
          style="width: 100%; max-height: 180px; border-radius: 8px; background: #000"
        />
      </el-form-item>
      <el-form-item label="演示链接">
        <el-input v-model="form.demoLink" placeholder="H5 预览地址（可选，手动填写）" />
      </el-form-item>
      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item label="热度">
            <el-input-number v-model="form.hotScore" class="!w-full" :min="0" :precision="0" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="排序">
            <el-input-number v-model="form.sort" class="!w-full" :min="0" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="状态">
        <el-radio-group v-model="form.status">
          <el-radio :value="0">上架</el-radio>
          <el-radio :value="1">下架</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="submit">确定</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import * as CategoryApi from '@/api/market/category'
import type { MarketCategoryVO } from '@/api/market/category'
import * as DictApi from '@/api/market/dict'
import * as MarketTemplateApi from '@/api/market/template'
import type { MarketTemplateVO } from '@/api/market/template'
import { UploadFile, UploadImg } from '@/components/UploadFile'

const message = useMessage()
const visible = ref(false)
const submitting = ref(false)
const formRef = ref()
const form = ref<MarketTemplateVO>({ status: 0, hotScore: 0, sort: 0 })

const categoryOptions = ref<MarketCategoryVO[]>([])
const festivalOptions = ref<Array<{ key: number; name: string }>>([])
const sceneOptions = ref<Array<{ key: number; name: string }>>([])
const industryOptions = ref<Array<{ key: number; name: string }>>([])

const rules = {
  activityKey: [{ required: true, message: '请选择主分类', trigger: 'change' }],
  name: [{ required: true, message: '名称必填', trigger: 'blur' }]
}

const emit = defineEmits<{ success: [] }>()

const categoryLabel = (cat: MarketCategoryVO) => {
  const alias = cat.alias ? ` / ${cat.alias}` : ''
  return `${cat.name}${alias}（ID ${cat.id}）`
}

const subTypeOptions = computed(() => {
  const cat = categoryOptions.value.find((c) => c.id === form.value.activityKey)
  if (!cat?.subClassifyJson?.length) return []
  return cat.subClassifyJson
    .map((sub) => ({
      id: Number(sub.id),
      name: String(sub.name || '')
    }))
    .filter((sub) => sub.id && sub.name)
})

const onActivityKeyChange = () => {
  const valid = subTypeOptions.value.some((s) => s.id === form.value.categoryKey)
  if (!valid) form.value.categoryKey = undefined
}

const isVideoUrl = (url?: string) => {
  if (!url) return false
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)
}

const isGifUrl = (url?: string) => {
  if (!url) return false
  return /\.gif(\?|$)/i.test(url)
}

const loadOptions = async () => {
  const [categories, festivals, scenes, industries] = await Promise.all([
    CategoryApi.getMarketCategoryList(),
    DictApi.getMarketFestivalList(),
    DictApi.getMarketSceneList(),
    DictApi.getMarketIndustryList()
  ])
  categoryOptions.value = (categories || []).filter((c) => c.status === 0)
  festivalOptions.value = (festivals || [])
    .filter((f: any) => f.status === 0)
    .map((f: any) => ({ key: f.festKey ?? f.key, name: f.name }))
  sceneOptions.value = (scenes || [])
    .filter((s: any) => s.status === 0)
    .map((s: any) => ({ key: s.sceneKey ?? s.key, name: s.name }))
  industryOptions.value = (industries || [])
    .filter((i: any) => i.status === 0)
    .map((i: any) => ({ key: i.industryKey ?? i.key, name: i.name }))
}

const open = async (id?: number) => {
  await loadOptions()
  if (id) {
    form.value = await MarketTemplateApi.getMarketTemplate(id)
  } else {
    form.value = { status: 0, hotScore: 0, sort: 0 }
  }
  visible.value = true
}

const submit = async () => {
  await formRef.value?.validate()
  submitting.value = true
  try {
    if (form.value.id) {
      await MarketTemplateApi.updateMarketTemplate(form.value)
    } else {
      await MarketTemplateApi.createMarketTemplate(form.value)
    }
    message.success('已保存')
    visible.value = false
    emit('success')
  } finally {
    submitting.value = false
  }
}

defineExpose({ open })
</script>
